import { useEffect, useRef, useState } from "react";

import { DateTime } from "luxon";

import type { BulkPaymentRowType, BulkPaymentForm } from "./BulkPayment.form";

export const EXPEDITE_CHOICE_MAPPER = {
  instant: 2,
  sameDay: 1,
  standard: 0,
} as const;

export const EXPEDITE_CHOICE_TO_SPEED: Record<
  number,
  "instant" | "sameDay" | "standard"
> = {
  [EXPEDITE_CHOICE_MAPPER.instant]: "instant",
  [EXPEDITE_CHOICE_MAPPER.sameDay]: "sameDay",
  [EXPEDITE_CHOICE_MAPPER.standard]: "standard",
};

export const EXPEDITE_OPTION_TO_CHOICE = {
  "": EXPEDITE_CHOICE_MAPPER.standard,
  sameDay: EXPEDITE_CHOICE_MAPPER.sameDay,
  fedNow: EXPEDITE_CHOICE_MAPPER.instant,
  fastest: EXPEDITE_CHOICE_MAPPER.instant,
} as const;

export const MAX_EXPEDITE_CHOICE = EXPEDITE_CHOICE_MAPPER.instant;

export type ExpediteSpeed = "instant" | "sameDay" | "standard" | "";
export type ExpediteOption = "fastest" | "sameDay" | "fedNow" | "";

type ResolveExpediteArgs = {
  // what the user asked for
  chosenExpediteOption?: ExpediteOption;
  // what this row is actually capable of
  speedStatus?: ExpediteSpeed;
  // the ceiling imposed by a template's max_expedite_choice, if any
  maxExpediteChoice?: number;
};

// resolves expedite_choice for each payment row
// returns 0 when user has not opted in for expedite,
// which is what makes an unchecked expedite card explicitly send standard
export const resolveExpediteChoice = ({
  chosenExpediteOption = "",
  speedStatus = "",
  maxExpediteChoice = MAX_EXPEDITE_CHOICE,
}: ResolveExpediteArgs): number =>
  Math.min(
    EXPEDITE_OPTION_TO_CHOICE[chosenExpediteOption],
    speedStatus ? EXPEDITE_CHOICE_MAPPER[speedStatus] : 0,
    maxExpediteChoice,
  );

// used to display speeds. returns "" when user has not opted into expediting,
// which callers treat as "show eligibility copy rather than a chosen speed"
export const resolveExpediteSpeed = (
  args: ResolveExpediteArgs,
): ExpediteSpeed => {
  if (!args.chosenExpediteOption || !args.speedStatus) return "";
  return EXPEDITE_CHOICE_TO_SPEED[resolveExpediteChoice(args)] ?? "";
};

// Resolve the entire batch before pagination, sharing each routing lookup.
export const getBulkPaymentSpeedStatuses = async (
  payments: BulkPaymentRowType[],
  features: {
    expedited_payments?: boolean;
    same_day_ach?: boolean;
    fednow_send?: boolean;
  },
  checkEligibility: (routingNumber: string) => Promise<boolean>,
  eligibilityCache: Map<string, Promise<boolean>>,
): Promise<ExpediteSpeed[]> => {
  const today = DateTime.now().startOf("day");
  const tomorrow = today.plus({ days: 1 });
  return Promise.all(
    payments.map(async (payment): Promise<ExpediteSpeed> => {
      const routingNumber = payment.recipient?.ach_destination?.routing_number;
      if (
        !features.expedited_payments ||
        !payment.recipient?.id ||
        !routingNumber
      ) {
        return "";
      }
      const date = DateTime.fromFormat(payment.scheduledDate, "MM/dd/yyyy");
      const isToday = date.hasSame(today, "day");
      const sameDay = Boolean(
        features.same_day_ach && (isToday || date.hasSame(tomorrow, "day")),
      );
      if (
        features.fednow_send &&
        isToday &&
        payment.transactionType === "send"
      ) {
        if (!eligibilityCache.has(routingNumber)) {
          eligibilityCache.set(
            routingNumber,
            checkEligibility(routingNumber).catch(() => {
              eligibilityCache.delete(routingNumber);
              return false;
            }),
          );
        }
        if (await eligibilityCache.get(routingNumber)) return "instant";
      }
      return sameDay ? "sameDay" : "standard";
    }),
  );
};

export const useBulkPaymentSpeeds = (
  form: ReturnType<typeof BulkPaymentForm.useForm>,
  features: Parameters<typeof getBulkPaymentSpeedStatuses>[1],
  checkEligibility: (routingNumber: string) => Promise<boolean>,
  maxExpediteChoice: number = MAX_EXPEDITE_CHOICE,
) => {
  const cache = useRef(new Map<string, Promise<boolean>>());
  const current = useRef({ form, features, checkEligibility });
  current.current = { form, features, checkEligibility };
  const signature = JSON.stringify([
    form.values.payments.map((payment) => [
      payment.recipient?.id,
      payment.recipient?.ach_destination?.routing_number,
      payment.scheduledDate,
      payment.transactionType,
    ]),
    form.values.chosenExpediteOption,
    features.expedited_payments,
    features.same_day_ach,
    features.fednow_send,
    maxExpediteChoice,
  ]);
  const [resolvedSignature, setResolvedSignature] = useState("");

  useEffect(() => {
    let cancelled = false;
    const snapshot = current.current;
    getBulkPaymentSpeedStatuses(
      snapshot.form.values.payments,
      snapshot.features,
      snapshot.checkEligibility,
      cache.current,
    ).then((speeds) => {
      if (cancelled) return;
      const latestForm = current.current.form;
      const payments = latestForm.values.payments.map((payment, index) => ({
        ...payment,
        speedStatus: speeds[index],
        speedStatusWithExpediteOption: resolveExpediteSpeed({
          chosenExpediteOption: latestForm.values.chosenExpediteOption,
          speedStatus: speeds[index],
          maxExpediteChoice,
        }),
      }));
      if (
        payments.some(
          (payment, index) =>
            payment.speedStatus !==
              latestForm.values.payments[index].speedStatus ||
            payment.speedStatusWithExpediteOption !==
              latestForm.values.payments[index].speedStatusWithExpediteOption,
        )
      ) {
        latestForm.setFieldValue("payments", payments);
      }
      setResolvedSignature(signature);
    });
    return () => {
      cancelled = true;
    };
  }, [signature, maxExpediteChoice]);

  return Boolean(
    features.expedited_payments && signature !== resolvedSignature,
  );
};
